import { v2Request } from './client';

/** Build legacy cartData shape for POST /api/cart/page (Object.keys exclusion). */
export function v2CartToLegacyCartData(cart) {
  const cartData = {
    totalAmount: Number(cart?.totalAmount) || 0,
    totalCount: Number(cart?.totalCount) || 0,
  };
  for (const line of cart?.products ?? []) {
    if (line?.productId) {
      cartData[line.productId] = { id: line.productId };
    }
  }
  return cartData;
}

/** Last 10 digits. Shorter values are omitted so the API can return loyalty: null. */
export function loyaltyPhone(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length < 10) return '';
  return digits.slice(-10);
}

function normalizeLoyaltyOffer(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const points = Math.floor(Number(raw.points) || 0);
  if (points <= 0) return null;
  const offer = { points };
  if (raw.customerId != null && raw.customerId !== '') offer.customerId = String(raw.customerId);
  if (raw.id != null && raw.id !== '') offer.id = String(raw.id);
  return offer;
}

/**
 * Cart page bundle: product suggestions plus usable loyalty points.
 * `billAmount` is the cart total before points. Do not read it from cartData.
 * @returns {Promise<{ suggestions: object[], loyalty: { points: number, customerId?: string, id?: string } | null }>}
 */
export async function getCartSuggestions(cart, { includeInCart = true, phone, billAmount } = {}) {
  const cartData = includeInCart
    ? {
        totalAmount: Number(cart?.totalAmount) || 0,
        totalCount: Number(cart?.totalCount) || 0,
      }
    : v2CartToLegacyCartData(cart);

  const amount = Number(billAmount ?? cart?.totalAmount);
  const body = {
    cartData,
    includeInCart,
    billAmount: Number.isFinite(amount) ? amount : 0,
  };
  const normalizedPhone = loyaltyPhone(phone);
  if (normalizedPhone) body.phone = normalizedPhone;

  const res = await v2Request('/api/cart/page', {
    method: 'POST',
    body,
  });
  const data = res?.data && typeof res.data === 'object' && !Array.isArray(res.data) ? res.data : {};
  return {
    suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
    loyalty: normalizeLoyaltyOffer(data.loyalty),
  };
}

/**
 * Ask Zithara to text a redemption OTP. Points are not deducted here.
 * @returns {Promise<{ sentTo: string, points: number }>}
 */
export async function initiateLoyaltyRedemption({ phone, points }) {
  const normalizedPhone = loyaltyPhone(phone);
  const usablePoints = Math.max(0, Math.floor(Number(points) || 0));
  if (!normalizedPhone || !usablePoints) {
    throw new Error('Sign in with a mobile number to use loyalty points.');
  }

  const res = await v2Request('/api/loyalty/initiate-redemption', {
    method: 'POST',
    body: { phone: normalizedPhone, points: usablePoints },
  });

  if (res?.success === false) {
    throw new Error(res.message || 'Could not send the loyalty OTP.');
  }

  return {
    sentTo: res?.data?.sentTo != null ? String(res.data.sentTo) : '',
    points: Math.max(0, Math.floor(Number(res?.data?.points) || usablePoints)),
  };
}
